/*
 * bv-review-schema-fix.js
 *
 * Bazaarvoice injects its review structured data into a script tag
 * (`#bv-jsonld-reviews-data`) on the client. Its payload looks like:
 *
 *   { "review": [ { headline, reviewBody, reviewRating: {"@type":"Rating", ...},
 *                   author: {"@type":"Person", ...}, ... } ],
 *     "@id": "...", "@context": "https://schema.org" }
 *
 * That is invalid for rich results: the container has no `@type` and the
 * reviews reference no product, so Search Console flags orphaned reviews.
 *
 * This component catches that script once BV injects it and rewrites it in
 * place as a single `Product` (the product the reviews belong to, chosen in
 * section settings) with every review nested in its `review` array. A Product
 * that carries multiple reviews must also include an `aggregateRating`, so we
 * fetch the product page and read the true average + count from Bazaarvoice's
 * summary script (`#bv-jsonld-bvloader-summary`) that lives there. If that
 * summary can't be fetched, we fall back to an aggregate derived from the
 * reviews actually in the markup (so `reviewCount` still honestly matches what
 * is marked up).
 *
 * It only re-shapes data BV already served — it never invents reviews, ratings,
 * or authors.
 *
 * Depends on BV's contract (flag if BV changes it):
 *   - reviews script id:  `bv-jsonld-reviews-data`      (override via `source-id`)
 *   - summary script id:  `bv-jsonld-bvloader-summary`  (on the PRODUCT page;
 *                                                        override via `summary-id`)
 *   - reviews live in a top-level `review` array; the summary contains an
 *     AggregateRating (or an `aggregateRating` property) with ratingValue + count
 *
 * Attributes:
 *   product-name  Product name the reviews belong to (required)
 *   product-url   Product absolute URL — fetched for the summary (required for aggregate)
 *   source-id     BV reviews script id (default: bv-jsonld-reviews-data)
 *   summary-id    BV summary script id (default: bv-jsonld-bvloader-summary)
 */
if (!customElements.get('bv-review-schema')) {
  customElements.define(
    'bv-review-schema',
    class BvReviewSchema extends HTMLElement {
      connectedCallback() {
        this.sourceId = this.getAttribute('source-id') || 'bv-jsonld-reviews-data';
        this.summaryId = this.getAttribute('summary-id') || 'bv-jsonld-bvloader-summary';
        this.productName = (this.getAttribute('product-name') || '').trim();
        this.productUrl = (this.getAttribute('product-url') || '').trim();
        this.cachedEntries = null; // original BV review entries, preserved across rewrites
        this.summaryAggregate = null; // true aggregate fetched from the product page
        this.summaryFetched = false;

        // BV injects the reviews script asynchronously (and may replace it), so
        // watch the document and re-fix on any change.
        this.observer = new MutationObserver(() => this.scan());
        this.observer.observe(document.documentElement, {
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.fetchSummary(); // async; re-runs scan() once the true aggregate resolves
        this.scan();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      scan() {
        const source = document.getElementById(this.sourceId);
        if (!source) return;

        const raw = (source.textContent || '').trim();
        if (!raw) return;

        let data;
        try {
          data = JSON.parse(raw);
        } catch (err) {
          return; // not valid JSON yet — leave it for a later mutation
        }

        // Resolve the BV review entries. If the script is already our rewritten
        // Product, reuse the original entries we cached (they still hold BV's
        // `headline` etc.); otherwise read + cache BV's `review` array.
        let entries;
        if (data && data['@type'] === 'Product' && Array.isArray(data.review)) {
          if (!this.cachedEntries) return;
          entries = this.cachedEntries;
        } else {
          entries = this.extractEntries(data);
          if (entries.length) this.cachedEntries = entries;
        }
        if (!entries || !entries.length) return;

        const reviews = entries.map((entry) => this.toReview(entry)).filter(Boolean);
        if (!reviews.length) return;

        const product = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: this.productName || document.title,
        };
        if (this.productUrl) product.url = this.productUrl;

        // A Product with multiple reviews must also carry an aggregateRating.
        const aggregate = this.summaryAggregate || this.computeAggregate(reviews);
        if (aggregate) product.aggregateRating = aggregate;

        product.review = reviews;

        const output = JSON.stringify(product);
        if (output === raw) return; // already correct — prevents a rewrite loop

        source.textContent = output;
      }

      // Fetch the product page once and read BV's summary aggregate from it.
      async fetchSummary() {
        if (this.summaryFetched || !this.productUrl) return;
        this.summaryFetched = true;

        try {
          const response = await fetch(this.productUrl, { credentials: 'same-origin' });
          if (!response.ok) return;

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const el = doc.getElementById(this.summaryId);
          if (!el) return;

          const data = JSON.parse((el.textContent || '').trim());
          const agg = this.findAggregate(data);
          if (!agg) return;

          const ratingValue = this.toNumber(agg.ratingValue);
          const reviewCount = this.toNumber(agg.reviewCount != null ? agg.reviewCount : agg.ratingCount);
          if (ratingValue == null || reviewCount == null || reviewCount <= 0) return;

          this.summaryAggregate = {
            '@type': 'AggregateRating',
            ratingValue: ratingValue,
            reviewCount: reviewCount,
            bestRating: this.toNumber(agg.bestRating) || 5,
            worstRating: this.toNumber(agg.worstRating) || 1,
          };
        } catch (err) {
          // Network/parse failure — the computed fallback in scan() still applies.
        } finally {
          this.scan(); // re-apply now that the true aggregate is (or isn't) available
        }
      }

      // BV nests reviews under a `review` array; tolerate a bare array too.
      extractEntries(data) {
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.review)) return data.review;
        return [];
      }

      // Map one BV review entry to a schema.org Review (nested under the Product,
      // so no @context / itemReviewed needed here — the parent supplies both).
      toReview(entry) {
        if (!entry || typeof entry !== 'object') return null;

        const review = { '@type': 'Review' };

        if (entry.headline) review.name = entry.headline;
        if (entry.reviewBody) review.reviewBody = entry.reviewBody;

        const datePublished = entry.datePublished || entry.dateCreated;
        if (datePublished) review.datePublished = datePublished;

        const authorName = entry.author && (entry.author.name || entry.author);
        if (authorName) review.author = { '@type': 'Person', name: authorName };

        const rating = entry.reviewRating;
        if (rating && rating.ratingValue != null) {
          review.reviewRating = {
            '@type': 'Rating',
            ratingValue: rating.ratingValue,
            bestRating: rating.bestRating != null ? rating.bestRating : 5,
            worstRating: rating.worstRating != null ? rating.worstRating : 1,
          };
        }

        return review;
      }

      // Find an AggregateRating (or aggregateRating property) anywhere in a node.
      findAggregate(node) {
        if (Array.isArray(node)) {
          for (let i = 0; i < node.length; i += 1) {
            const found = this.findAggregate(node[i]);
            if (found) return found;
          }
          return null;
        }
        if (node && typeof node === 'object') {
          if (node['@type'] === 'AggregateRating' && node.ratingValue != null) return node;
          if (node.aggregateRating) {
            const found = this.findAggregate(node.aggregateRating);
            if (found) return found;
          }
          const keys = Object.keys(node);
          for (let i = 0; i < keys.length; i += 1) {
            if (keys[i] === '@type') continue;
            const found = this.findAggregate(node[keys[i]]);
            if (found) return found;
          }
        }
        return null;
      }

      // Fallback: derive an aggregate from the reviews present in the markup.
      computeAggregate(reviews) {
        const rated = reviews.filter(
          (review) => review.reviewRating && review.reviewRating.ratingValue != null
        );
        if (!rated.length) return null;

        const sum = rated.reduce((total, review) => total + Number(review.reviewRating.ratingValue), 0);
        return {
          '@type': 'AggregateRating',
          ratingValue: Math.round((sum / rated.length) * 10) / 10,
          reviewCount: reviews.length,
          bestRating: 5,
          worstRating: 1,
        };
      }

      toNumber(value) {
        if (value == null) return null;
        const num = parseFloat(String(value).replace(/,/g, ''));
        return isNaN(num) ? null : num;
      }
    }
  );
}
